import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  model,
  OnInit, signal,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent, DateOptions,
  FormComponent,
  ModalComponent,
  ModalOptions
} from '@shared/components';
import {DropdownComponent, DropdownOptions} from '@shared/components/dropdown/dropdown.component';
import {NgOptimizedImage} from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NgForm,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {VaccinationCoding} from '@core/classes/other/vaccination-coding';
import {APIService, DataService, ToastService} from '@core/services';
import {PatientService} from '@core/services/patient.service';
import {HTTPMethods, ModalTypes} from '@core/enums';
import {HttpHeaders} from '@angular/common/http';
import {assets} from '@core/constants';
import {VaccinesSearchComponent} from '@shared/components/dropdown/search/vaccines-search/vaccines-search.component';
import {Vaccination} from '@core/classes/tables/vaccination';
import {ResponseSingle} from '@core/classes/other/response';
import {TranslateService, Translation} from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {checkDate} from '@core/helpers';
import moment from 'moment';

@UntilDestroy()
@Component({
  selector: 'ncp-add-vaccination',
  imports: [
    ButtonComponent,
    DateComponent,
    DropdownComponent,
    ModalComponent,
    NgOptimizedImage,
    ReactiveFormsModule
  ],
  templateUrl: './add-vaccination.component.html',
  styleUrl: './add-vaccination.component.scss',
})
export class AddVaccinationComponent implements OnInit, AfterViewInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('vFrm') protected vaccinationsFormEl?: NgForm;
  @ViewChild('addVaccinationButton') public addVaccinationButton!: ButtonComponent;

  @ViewChild( 'vaccinationDesc') public vaccinationDesc!: DropdownComponent<VaccinationCoding>;
  @ViewChild('vaccineDate') public vaccineDate!: DateComponent;

  @ViewChild('vaccinesTemplate') public vaccinesTemplate!: TemplateRef<VaccinationCoding>;

  private readonly translate = inject(TranslateService);
  protected translations: Translation = this.translate.instant('patient.ps.vaccinations');
  protected readonly toastService = inject(ToastService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private apiService = inject(APIService);
  private patientService = inject(PatientService);

  protected data = model<any>();

  protected form: FormGroup = new FormGroup({
    vaccination_id: new FormControl<number | string | undefined>(undefined, [Validators.required]),
    vaccination_date: new FormControl<string>('', [Validators.required]),
  }, {
    validators: [this.formValidator()]
  });

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      let vaccinationId = (control as FormGroup).controls['vaccination_id'];
      let vaccinationDate = (control as FormGroup).controls['vaccination_date'];

      let patientDOB = moment(this.patientService.data?.dob, ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY'], true);
      let today = moment();

      if(vaccinationId.value === undefined || vaccinationId.value === null) {
        vaccinationId.setErrors({v_vaccinationIdInvalid: true});
        errors['v_vaccinationIdInvalid'] = true;
      }

      if((!checkDate(vaccinationDate.value).isValid())) {
        vaccinationDate.setErrors({v_vaccinationDateInvalid: true});
        errors['v_vaccinationDateInvalid'] = true;
      } else {
        if(checkDate(vaccinationDate.value).isBefore(patientDOB)) {
          vaccinationDate.setErrors({v_vaccinationDateAfterDOB: true});
          errors['v_vaccinationDateAfterDOB'] = true;
        } else if(checkDate(vaccinationDate.value).isSameOrAfter(today)) {
          vaccinationDate.setErrors({v_vaccinationDateBeforeToday: true});
          errors['v_vaccinationDateBeforeToday'] = true;
        } else {
          vaccinationDate.setErrors(null);
        }
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  public btnVacOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'ndaBtn',
      tooltip: 'add_vaccination',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      type: ModalTypes.form,
      form: FormComponent,
      title: 'add_vaccination',
      options: {
        animation: true,
        size: 'xl',
        fullscreen: "lg",
        backdrop: 'static',
        keyboard: false,
      },
      onClose: () => {this.onFormReset()}
    })
  );

  protected vaccinationDescDDOptions = signal<DropdownOptions<VaccinationCoding>>(
    new DropdownOptions<VaccinationCoding>({
      id: 'vaccinationDescription',
      name: 'vaccinationDescription',
      placeholder: 'vaccination_description',
      //itemTemplate: this.vaccinesTemplate,
      dataId: 'id',
      label: {
        id: "vaccination_desc_label",
        text: 'vaccination_description',
      },
      api: {
        description: {
          path: '/get-vaccines_by_description',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            description: ''
          }
        },
        brandName: {
          path: '/get-vaccines_by_brand_name',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            description: ''
          }
        }
      },
      required: true,
      searchComponent: VaccinesSearchComponent,
      formControl: this.form.get('vaccination_id') as FormControl
    })
  );

  protected vaccinationDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'vaccinationDate',
      label: {
        text: 'vaccination_date',
      },
      name: 'vaccinationDate',
      initialValue: '',
      formControl: this.form.get('vaccination_date') as FormControl,
      required: true,
      minDate: this.patientService.patient?.data?.dob,
      maxDate: moment().format('YYYY-MM-DD'),
    })
  );

  ngOnInit() {
    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('patient.ps.vaccinations');
    });

    this.btnVacOptions().onClick = (event) => {
      this.mod.options().onReset = this.onFormReset;
      let vaccinesOptions = this.vaccinationDescDDOptions();
      vaccinesOptions.itemTemplate = this.vaccinesTemplate;
      this.vaccinationDescDDOptions.set(vaccinesOptions);
      this.vaccinationDesc.detectChanges();
      this.addVaccinationButton.blur();
      this.mod.open();
    };
    //this.vaccinationDateOptions().formControl = (this.form.get('vaccination_date') as FormControl);
    this.modOptions().onSave = this.onSave;
  }

  onFormReset = () => {
    console.log('on reset form');
    this.form.reset();
    this.vaccinationDesc.clearSelection();
    this.vaccineDate.reset();

    this.modOptions().errors = [];
  }

  ngAfterViewInit(): void {

  }

  onSave = () => {
    this.vaccinationsFormEl?.ngSubmit.emit();
  }

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      this.form.controls[control].markAsTouched();
    })

    return true;
  }

  onFormSubmit = () => {
    if(this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modOptions().disableOnSave = true;
    let body = {
      vaccination_date: (this.form.get('vaccination_date') as FormControl).value,
      vaccination_id: this.vaccinationDescDDOptions().value!.id,
      status: 1,
      version_id: this.patientService.version!.new,
    }

    this.apiService.call({
      path: '/vaccinations',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      type: HTTPMethods.POST,
      body: body
    })?.subscribe((res: ResponseSingle<Vaccination>) => {
      if(res && res.status?.toString() === '1') {
        let data = this.data()?.tblOptions().data;
        data.unshift(res.data);
        if (this.data && this.data() && this.data().tbl && this.data().tbl.tableData) {
          this.data()!.tbl!.tableData! = data;
        }
        this.mod.close();
      }
    })
  }

  protected readonly assets = assets;
}
